import { memo, useCallback } from 'react';
import { NodeProps } from '@xyflow/react';
import { useNodeStore } from '@/stores/node-store';
import { getGroupChromeState } from '@/pages/workflow/helper/group-nodes';
import { selectGroupChildren } from '@/pages/workflow/hooks/use-node-grouping';
import { cn } from '@/lib/utils';

function GroupNode({ id }: NodeProps) {
  const chromeState = useNodeStore((state) =>
    getGroupChromeState(state.nodes, id, state.groupContainerFocusedId),
  );
  const setGroupContainerFocusedId = useNodeStore(
    (state) => state.setGroupContainerFocusedId,
  );

  const handleContainerPointerDown = useCallback(
    (event: React.PointerEvent) => {
      event.stopPropagation();
      setGroupContainerFocusedId(id);
      // Select this group's children so keyboard Delete/Backspace and the
      // Ungroup toolbar's actions (rendered by UngroupToolbarOverlay in
      // workflow-editor-page.tsx) target the same nodes, instead of leaving
      // whatever was selected elsewhere on the canvas active. Marks the
      // change as focus-triggered so onSelectionChange doesn't treat it
      // like a direct click on one of this group's children (which should
      // still clear focus and switch to the child-selected chrome).
      selectGroupChildren(id);
    },
    [id, setGroupContainerFocusedId],
  );

  const chromeClass =
    chromeState === 'container-focused'
      ? 'group-node-chrome--container-focused'
      : chromeState === 'child-selected'
        ? 'group-node-chrome--child-selected'
        : 'group-node-chrome--idle';

  return (
    <div className='relative h-full w-full'>
      <div
        className={cn('group-node-drag-surface absolute inset-0', chromeClass)}
        onPointerDown={handleContainerPointerDown}
      />
    </div>
  );
}

export default memo(GroupNode);
